import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    Paper,
    TextField,
    Typography,
    Divider,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";

import {
    enviarDatosSorteo,
    getSorteoActivo,
    verificarTicket,
} from "../servicios/axios.service";

const initialForm = {
    nombre: "",
    apellidos: "",
    email: "",
    emailConfirmacion: "",
    telefonoMovil: "",
    serieTicket: "",
    numeroTicket: "",
    importe: "",
    aceptaPrivacidad: false,
    aceptaComunicaciones: false,
};
const Formulario = () => {
    const [formData, setFormData] = useState(initialForm);
    const [enviado, setEnviado] = useState(false);
    const [paso, setPaso] = useState(1);
    const [verificando, setVerificando] = useState(false);
    const [errorVerificacion, setErrorVerificacion] = useState("");
    const [numerosAsignados, setNumerosAsignados] = useState(0);
    const [sorteoActivo, setSorteoActivo] = useState(null);
    const [idtick, setIdtick] = useState(null);
    const [enviandoDatos, setEnviandoDatos] = useState(false);
    const [mostrarNuevoTicket, setMostrarNuevoTicket] = useState(false);
    const [datosPaso2Guardados, setDatosPaso2Guardados] = useState(null);
    const [envioExitoso, setEnvioExitoso] = useState(false);
    const [modalBasesAbierto, setModalBasesAbierto] = useState(false);

    const abrirModalBases = () => setModalBasesAbierto(true);
    const cerrarModalBases = () => setModalBasesAbierto(false);

    const basesHtml = useMemo(() => {
        const html = sorteoActivo?.bases;
        return typeof html === "string" ? html : "";
    }, [sorteoActivo]);

    const hayBases = Boolean(basesHtml.trim());

    useEffect(() => {
        let ignorar = false;

        const cargarSorteo = async () => {
            try {
                const respuesta = await getSorteoActivo();
                if (!ignorar && respuesta && respuesta.ok && respuesta.data) {
                    setSorteoActivo(respuesta.data);
                }
            } catch (error) {
                // Silencio: el formulario puede operar sin encabezado detallado.
            }
        };

        cargarSorteo();

        return () => {
            ignorar = true;
        };
    }, []);

    const encabezado = useMemo(() => {
        if (!sorteoActivo) {
            return "Formulario";
        }
        const { descripcion, fecha_inicio, fecha_fin, euros_para_numero } =
            sorteoActivo;
        if (!descripcion || !fecha_inicio || !fecha_fin || !euros_para_numero) {
            return "Formulario";
        }
        return `Sorteo "${descripcion}". Tickets válidos del ${fecha_inicio} al ${fecha_fin}. Un número por cada ${euros_para_numero} € de compra`;
    }, [sorteoActivo]);

    const errorImporte = useMemo(() => {
        if (!formData.importe.trim()) {
            return "";
        }
        const valor = Number(formData.importe);
        if (!Number.isFinite(valor)) {
            return "El importe debe ser un numero valido";
        }
        const dosDecimales = /^\d+(\.\d{1,2})?$/.test(formData.importe);
        if (!dosDecimales) {
            return "El importe debe tener hasta dos decimales";
        }
        return "";
    }, [formData.importe]);

    const errores = useMemo(() => {
        if (paso !== 2) {
            return {};
        }
        const nuevosErrores = {};

        if (!formData.nombre.trim()) {
            nuevosErrores.nombre = "El nombre es obligatorio";
        }

        if (!formData.apellidos.trim()) {
            nuevosErrores.apellidos = "Los apellidos son obligatorios";
        }

        if (!formData.email.trim()) {
            nuevosErrores.email = "El email es obligatorio";
        }

        if (!formData.emailConfirmacion.trim()) {
            nuevosErrores.emailConfirmacion =
                "Debes volver a escribir el email";
        } else if (
            formData.email.trim() !== formData.emailConfirmacion.trim()
        ) {
            nuevosErrores.emailConfirmacion = "Los emails no coinciden";
        }

        if (!formData.telefonoMovil.trim()) {
            nuevosErrores.telefonoMovil = "El teléfono móvil es obligatorio";
        }

        if (!formData.aceptaPrivacidad) {
            nuevosErrores.aceptaPrivacidad =
                "Debes aceptar la política de privacidad";
        }

        if (!formData.aceptaComunicaciones) {
            nuevosErrores.aceptaComunicaciones =
                "Debes aceptar que podamos ponernos en contacto contigo";
        }

        return nuevosErrores;
    }, [formData, paso]);

    const puedeEnviarPaso2 = useMemo(() => {
        if (paso !== 2) {
            return false;
        }
        const camposCompletos =
            formData.nombre.trim() &&
            formData.apellidos.trim() &&
            formData.email.trim() &&
            formData.emailConfirmacion.trim() &&
            formData.telefonoMovil.trim() &&
            formData.aceptaPrivacidad &&
            formData.aceptaComunicaciones;

        return Boolean(
            camposCompletos &&
            Object.keys(errores).length === 0 &&
            idtick != null &&
            !enviandoDatos,
        );
    }, [enviandoDatos, errores, formData, idtick, paso]);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        if (type === "checkbox") {
            setFormData((prev) => ({ ...prev, [name]: checked }));
            return;
        }
        if (name === "serieTicket") {
            const limpio = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
            setFormData((prev) => ({ ...prev, [name]: limpio }));
            return;
        }
        if (name === "numeroTicket") {
            const limpio = value.replace(/\D/g, "");
            setFormData((prev) => ({ ...prev, [name]: limpio }));
            return;
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleVerificar = async () => {
        if (verificando) {
            return;
        }
        setErrorVerificacion("");
        setVerificando(true);
        try {
            const respuesta = await verificarTicket(
                formData.serieTicket.trim(),
                formData.numeroTicket.trim(),
                formData.importe.trim(),
            );
            console.log(respuesta);
            if (respuesta && respuesta.numeros && respuesta.idtick) {
                setNumerosAsignados(respuesta.numeros);
                setIdtick(respuesta.idtick);
                setPaso(2);
            } else {
                setNumerosAsignados(0);
                setIdtick(null);
                if (respuesta.mensaje) {
                    setErrorVerificacion(respuesta.mensaje);
                } else {
                    if (respuesta.idtick) {
                        setErrorVerificacion(
                            "El ticket no cumple los requisitos para participar",
                        );
                    }
                }
            }
        } catch (error) {
            setErrorVerificacion("No se pudo verificar el ticket.");
        } finally {
            setVerificando(false);
        }
    };
    const handleSubmit = async (event) => {
        event.preventDefault();
        setMostrarNuevoTicket(false);
        setEnvioExitoso(false);

        if (Object.keys(errores).length > 0) {
            setErrorVerificacion("Debes corregir los errores del formulario.");
            setMostrarNuevoTicket(true);
            return;
        }
        if (idtick == null) {
            setErrorVerificacion(
                "No se pudo validar el ticket. Verifícalo de nuevo antes de enviar.",
            );
            setMostrarNuevoTicket(true);
            return;
        }

        if (enviandoDatos) {
            return;
        }
        setErrorVerificacion("");
        setEnviandoDatos(true);
        try {
            const respuesta = await enviarDatosSorteo(
                formData.nombre.trim(),
                formData.apellidos.trim(),
                formData.email.trim(),
                formData.telefonoMovil.trim(),
                formData.aceptaPrivacidad,
                formData.aceptaComunicaciones,
                idtick,
            );
            console.log(respuesta);
            if (!respuesta) {
                setEnviado(false);
                setErrorVerificacion("No se pudo enviar la solicitud.");
                setMostrarNuevoTicket(true);
                return;
            }
            if (respuesta.ok !== true) {
                setEnviado(false);
                setErrorVerificacion(
                    respuesta.mensaje || "No se pudo enviar la solicitud.",
                );
                setMostrarNuevoTicket(true);
                return;
            }
            console.log("Formulario enviado:", respuesta);
            setDatosPaso2Guardados({
                nombre: formData.nombre,
                apellidos: formData.apellidos,
                email: formData.email,
                emailConfirmacion: formData.emailConfirmacion,
                telefonoMovil: formData.telefonoMovil,
                aceptaPrivacidad: false,
                aceptaComunicaciones: false,
            });
            setFormData(initialForm);
            setEnviado(true);
            setEnvioExitoso(true);
            setMostrarNuevoTicket(true);
        } catch (error) {
            console.log(error);
            setEnviado(false);
            setErrorVerificacion("No se pudo enviar la solicitud.");
            setMostrarNuevoTicket(true);
        } finally {
            setEnviandoDatos(false);
        }
    };

    const handleNuevoTicket = () => {
        const datosGuardados = datosPaso2Guardados || {};
        setPaso(1);
        setEnviado(false);
        setErrorVerificacion("");
        setNumerosAsignados(0);
        setIdtick(null);
        setMostrarNuevoTicket(false);
        setEnvioExitoso(false);
        setFormData({
            ...initialForm,
            nombre: datosGuardados.nombre || "",
            apellidos: datosGuardados.apellidos || "",
            email: datosGuardados.email || "",
            emailConfirmacion: datosGuardados.emailConfirmacion || "",
            telefonoMovil: datosGuardados.telefonoMovil || "",
            aceptaPrivacidad: Boolean(datosGuardados.aceptaPrivacidad),
            aceptaComunicaciones: Boolean(datosGuardados.aceptaComunicaciones),
        });
    };

    return (
        <Paper elevation={3} sx={{ p: 3, mt: 3, width: "100%", maxWidth: 650 }}>
            <Typography
                variant="h5"
                component="h1"
                // mb={2}
                sx={{ textAlign: "center" }}
            >
                {encabezado}
            </Typography>

            <Box component="form" noValidate onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Divider
                            variant="middle"
                            sx={{
                                my: 1,
                                borderBottomWidth: 1,
                                borderColor: "primary.main",
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography
                            variant="body2"
                            component="p"
                            sx={{ textAlign: "center" }}
                        >
                            {paso === 1
                                ? "Introduza su número de ticket, tiene esta forma: (VNAGR NNNNNN) y el importe. Despues pulse en verificar para continuar"
                                : `${numerosAsignados === 1 ? "Le corresponde 1 número para el sorteo. Complete todos los campos para recibirlo en su correo electrónico" : `Le corresponden ${numerosAsignados} números para el sorteo. Complete todos los campos para recibirlos en su correo electrónico`}`}
                        </Typography>
                        <Typography sx={{ textAlign: "center", mt: 1 }}>
                            <Button
                                variant="text"
                                size="small"
                                onClick={abrirModalBases}
                                disabled={!hayBases}
                                sx={{ textTransform: "none" }}
                            >
                                Consultar bases
                            </Button>
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider
                            variant="middle"
                            sx={{
                                my: 1,
                                borderBottomWidth: 1,
                                borderColor: "primary.main",
                            }}
                        />
                    </Grid>
                    {paso === 1 ? (
                        <>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    name="serieTicket"
                                    label="Serie"
                                    value={formData.serieTicket}
                                    onChange={handleChange}
                                    helperText="V3AGR"
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    name="numeroTicket"
                                    label="Número"
                                    value={formData.numeroTicket}
                                    onChange={handleChange}
                                    helperText="123456"
                                    inputProps={{ inputMode: "numeric" }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    required
                                    name="importe"
                                    label="Importe"
                                    placeholder="0.00"
                                    value={formData.importe}
                                    onChange={handleChange}
                                    error={Boolean(errorImporte)}
                                    helperText={
                                        errorImporte ||
                                        "Usa punto decimal, por ejemplo 10.50"
                                    }
                                />
                            </Grid>
                            {errorVerificacion && (
                                <Snackbar
                                    open={Boolean(errorVerificacion)}
                                    autoHideDuration={4000}
                                    onClose={(_, reason) => {
                                        if (reason === "clickaway") {
                                            return;
                                        }
                                        setErrorVerificacion("");
                                    }}
                                    sx={{
                                        position: "static",
                                        transform: "none",
                                    }}
                                >
                                    <Alert
                                        onClose={() => setErrorVerificacion("")}
                                        severity="error"
                                        sx={{ mb: 2 }}
                                    >
                                        {errorVerificacion}
                                    </Alert>
                                </Snackbar>
                            )}

                            <Grid item xs={12}>
                                <Button
                                    type="button"
                                    variant="contained"
                                    onClick={handleVerificar}
                                    disabled={
                                        verificando ||
                                        !formData.serieTicket.trim() ||
                                        !formData.numeroTicket.trim() ||
                                        !formData.importe.trim() ||
                                        Boolean(errorImporte)
                                    }
                                >
                                    {verificando
                                        ? "Verificando..."
                                        : "Verificar"}
                                </Button>
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    required
                                    name="nombre"
                                    label="Nombre"
                                    value={formData.nombre}
                                    onChange={handleChange}
                                    error={Boolean(
                                        enviado &&
                                        !envioExitoso &&
                                        errores.nombre,
                                    )}
                                    helperText={
                                        enviado &&
                                        !envioExitoso &&
                                        errores.nombre
                                    }
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    required
                                    name="apellidos"
                                    label="Apellidos"
                                    value={formData.apellidos}
                                    onChange={handleChange}
                                    error={Boolean(
                                        enviado &&
                                        !envioExitoso &&
                                        errores.apellidos,
                                    )}
                                    helperText={
                                        enviado &&
                                        !envioExitoso &&
                                        errores.apellidos
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    type="email"
                                    name="email"
                                    label="Email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    error={Boolean(
                                        enviado &&
                                        !envioExitoso &&
                                        errores.email,
                                    )}
                                    helperText={
                                        enviado &&
                                        !envioExitoso &&
                                        errores.email
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    type="email"
                                    name="emailConfirmacion"
                                    label="Repetir email"
                                    value={formData.emailConfirmacion}
                                    onChange={handleChange}
                                    error={Boolean(
                                        enviado &&
                                        !envioExitoso &&
                                        errores.emailConfirmacion,
                                    )}
                                    helperText={
                                        enviado &&
                                        !envioExitoso &&
                                        errores.emailConfirmacion
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    name="telefonoMovil"
                                    label="Telefono movil"
                                    value={formData.telefonoMovil}
                                    onChange={handleChange}
                                    error={Boolean(
                                        enviado &&
                                        !envioExitoso &&
                                        errores.telefonoMovil,
                                    )}
                                    helperText={
                                        enviado &&
                                        !envioExitoso &&
                                        errores.telefonoMovil
                                    }
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="aceptaPrivacidad"
                                            checked={formData.aceptaPrivacidad}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="He leído y acepto la Política de Privacidad."
                                />
                                {enviado &&
                                    !envioExitoso &&
                                    errores.aceptaPrivacidad && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: "error.main",
                                                display: "block",
                                            }}
                                        >
                                            {errores.aceptaPrivacidad}
                                        </Typography>
                                    )}
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="aceptaComunicaciones"
                                            checked={
                                                formData.aceptaComunicaciones
                                            }
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Acepto recibir comunicaciones comerciales y promociones a traves de correo electronico y/o SMS."
                                />
                                {enviado &&
                                    !envioExitoso &&
                                    errores.aceptaComunicaciones && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: "error.main",
                                                display: "block",
                                            }}
                                        >
                                            {errores.aceptaComunicaciones}
                                        </Typography>
                                    )}
                            </Grid>

                            {errorVerificacion && (
                                <Snackbar
                                    open={Boolean(errorVerificacion)}
                                    autoHideDuration={4000}
                                    onClose={(_, reason) => {
                                        if (reason === "clickaway") {
                                            return;
                                        }
                                        setErrorVerificacion("");
                                    }}
                                    sx={{
                                        position: "static",
                                        transform: "none",
                                    }}
                                >
                                    <Alert
                                        severity="error"
                                        onClose={() => setErrorVerificacion("")}
                                        sx={{ mb: 2 }}
                                    >
                                        {errorVerificacion}
                                    </Alert>
                                </Snackbar>
                            )}

                            {envioExitoso && (
                                <Snackbar
                                    open={envioExitoso}
                                    autoHideDuration={4000}
                                    onClose={(_, reason) => {
                                        if (reason === "clickaway") {
                                            return;
                                        }
                                        setEnvioExitoso(false);
                                    }}
                                    sx={{
                                        position: "static",
                                        transform: "none",
                                    }}
                                >
                                    <Alert
                                        severity="success"
                                        onClose={() => setEnvioExitoso(false)}
                                        sx={{ mb: 2 }}
                                    >
                                        Formulario enviado correctamente.
                                        Compruebe su correo electronico para
                                        acceder a los numeros conseguidos
                                    </Alert>
                                </Snackbar>
                            )}

                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={!puedeEnviarPaso2}
                                >
                                    {enviandoDatos ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    ) : (
                                        "Recibir mis números"
                                    )}
                                </Button>
                            </Grid>
                            {mostrarNuevoTicket && (
                                <Grid item xs={12}>
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        onClick={handleNuevoTicket}
                                    >
                                        Introducir nuevo ticket
                                    </Button>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{
                                        color: "text.secondary",
                                        fontSize: "0.9em",
                                    }}
                                >
                                    Informacion basica sobre proteccion de
                                    datos: <br></br>
                                    <strong>Responsable:</strong> Alimentación
                                    Diferente S.A. <br></br>
                                    <strong>Finalidad:</strong> Envio de
                                    comunicaciones comerciales, novedades y
                                    promociones sobre nuestros servicios.
                                    <br></br>
                                    <strong>Legitimacion:</strong> Tu
                                    consentimiento expreso.
                                    <br></br>
                                    <strong>Destinatarios:</strong> No se
                                    cederan datos a terceros, salvo obligación
                                    legal o proveedores de servicios de
                                    email/SMS.
                                    <br></br>
                                    <strong>Derechos:</strong> Puedes retirar tu
                                    consentimiento y acceder, rectificar o
                                    suprimir tus datos escribiendo a
                                    arco@diferente.es. <br></br>
                                    <strong>Información adicional:</strong>{" "}
                                    Consulta nuestra{" "}
                                    <a
                                        href="https://diferente.es/politica-de-privacidad/"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Política de Privacidad
                                    </a>
                                    .
                                </Typography>
                            </Grid>
                        </>
                    )}
                </Grid>
            </Box>

            <Dialog
                open={modalBasesAbierto}
                onClose={cerrarModalBases}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Bases del sorteo</DialogTitle>

                <DialogContent dividers>
                    {hayBases ? (
                        <Box
                            sx={{
                                "& img": { maxWidth: "100%" },
                                "& a": { wordBreak: "break-word" },
                            }}
                            dangerouslySetInnerHTML={{ __html: basesHtml }}
                        />
                    ) : (
                        <Typography variant="body2">
                            No hay bases disponibles para este sorteo.
                        </Typography>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={cerrarModalBases} variant="contained">
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default Formulario;
